import { useEffect, useState } from 'react'
import useHttpLoader from '../hooks/useHttpLoader'
import { IUser } from '@/pages/Users/users.types'
import usersApi from '@/pages/Users/users.api'

/**
 * Хук для загрузки аватара пользователя и управления его состоянием.
 *
 * - Загружает blob-аватар по userId (и fileName, если указан).
 * - Возвращает ссылку на blob, флаг загрузки и ошибку.
 * - Очищает blob-URL при размонтировании или смене userId/fileName.
 * - Сбрасывает ошибку при успешной загрузке нового аватара.
 *
 * @param userId ID пользователя, чей аватар нужно загрузить.
 * @param fileName Имя файла аватара (опционально, для обновления при смене файла).
 * @returns {
 *   url: string | null — ссылка на blob-аватар,
 *   loading: boolean — признак загрузки,
 *   error: boolean — ошибка загрузки
 * }
 *
 * Пример:
 *   const { url, loading, error } = useAvatarCtrl(userId, fileName)
 */
const useAvatarCtrl = (userId: IUser['id'], fileName?: string) => {
  const [error, setError] = useState(false)
  const { wait, loading } = useHttpLoader()
  const [url, setUrl] = useState<string>(null)

  useEffect(() => {
    if (!userId) return
    let currentUrl = null
    wait(usersApi.getUserAvatar(userId), (resp) => {
      if (resp.status === 'success') {
        currentUrl = URL.createObjectURL(resp.body)
        setUrl(currentUrl)
        setError(false)
      } else setError(true)
    })
    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl)
    }
  }, [userId, fileName])

  return { url, loading, error }
}

export default useAvatarCtrl
